import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, ChevronLeft, ChevronRight, Wallet, Plus } from 'lucide-react';
import Toast from './Toast';
import { userService } from '../services/userService';
import { bankService } from '../services/bankService';
import { withdrawService } from '../services/withdrawService';
import { useTranslation } from '../i18n/LocaleContext';

// 平台打款钱包只有波场一条链，网络列表收敛到这里；接了 ETH/BSC 收款钱包再往这里加。
const USDT_NETWORKS = [['TRC20', 'Tron']];

const WithdrawModal = ({ isOpen, onClose }) => {
  const { t, language } = useTranslation();
  const [formData, setFormData] = useState({
    amount: '',
    withdrawPin: '',
    selectedBankId: '',
    withdrawMethod: 'bank',
    walletAddress: '',
    network: 'TRC20'
  });
  const [userProfile, setUserProfile] = useState(null);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [binding, setBinding] = useState(false);
  const [savingBind, setSavingBind] = useState(false);
  const [bindForm, setBindForm] = useState({ kind: 'card', account_number: '', account_name: '', network: 'TRC20', walletAddress: '' });

  useEffect(() => {
    if (isOpen) {
      setBinding(false);
      fetchData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setAnimating(true), 10);
    } else {
      setAnimating(false);
      setTimeout(() => setVisible(false), 300);
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch profile để lấy balance
      const profileData = await userService.getProfile();
      setUserProfile(profileData.profile);

      // Fetch banks
      const banksData = await bankService.getBanks();
      setBanks(banksData.banks);

      // 银行页签只认卡；USDT 绑定用于 USDT 页签回填
      const cardBanks = banksData.banks.filter((b) => b.account_type !== 'usdt');
      if (cardBanks.length > 0) {
        setFormData(prev => ({
          ...prev,
          selectedBankId: cardBanks[0].id.toString()
        }));
      }
      const usdtBank = banksData.banks.find((b) => b.account_type === 'usdt');
      if (usdtBank) {
        const net = USDT_NETWORKS.find(([n]) => String(usdtBank.bank_name).includes(n))?.[0] || 'TRC20';
        setFormData(prev => ({
          ...prev,
          walletAddress: prev.walletAddress || usdtBank.account_number,
          network: net,
          // 只绑了 USDT 的用户直接进 USDT 页签
          withdrawMethod: cardBanks.length === 0 ? 'usdt' : prev.withdrawMethod
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const cardBanks = banks.filter((b) => b.account_type !== 'usdt');
  const usdtBound = banks.some((b) => b.account_type === 'usdt');
  const selectedBank = cardBanks.find(bank => bank.id.toString() === formData.selectedBankId) || cardBanks[0];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userProfile) {
      setToast({ message: t('cannotFetchProfile'), type: 'error' });
      return;
    }

    if (parseFloat(formData.amount) > userProfile.balance) {
      setToast({ message: t('insufficientBalance'), type: 'error' });
      return;
    }

    if (parseFloat(formData.amount) < 50) {
      setToast({ message: t('minimumWithdraw'), type: 'error' });
      return;
    }

    if (formData.withdrawPin.length !== 6) {
      setToast({ message: t('pinMustBeSixDigits'), type: 'error' });
      return;
    }

    if (formData.withdrawMethod === 'bank' && !selectedBank) {
      setToast({ message: t('selectRecipientRequired'), type: 'error' });
      return;
    }
    if (formData.withdrawMethod === 'usdt' && !formData.walletAddress.trim()) {
      setToast({ message: t('enterUsdtAddressRequired'), type: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      await withdrawService.createWithdraw({
        bank_id: formData.withdrawMethod === 'bank' ? parseInt(formData.selectedBankId) : undefined,
        withdraw_method: formData.withdrawMethod,
        wallet_address: formData.withdrawMethod === 'usdt' ? formData.walletAddress.trim() : undefined,
        network: formData.withdrawMethod === 'usdt' ? formData.network : undefined,
        amount: parseFloat(formData.amount),
        withdraw_pin: formData.withdrawPin
      });

      setToast({ message: t('withdrawRequested'), type: 'success' });

      setTimeout(() => {
        onClose();
        setFormData({ amount: '', withdrawPin: '', selectedBankId: '', withdrawMethod: 'bank', walletAddress: '', network: 'TRC20' });
      }, 2000);
    } catch (error) {
      const errorData = error.response?.data || {};
      const errorCode = errorData.errorCode;
      const apiError = error.message || errorData.error || '';
      let errorMessage = apiError;

      // Handle error codes from backend
      if (errorCode === 'ORDERS_NOT_COMPLETED') {
        const total = errorData.totalOrders || 0;
        const remaining = errorData.remainingOrders || 0;
        errorMessage = t('ordersNotCompleted')
          .replace('{total}', total)
          .replace('{remaining}', remaining);
      } else if (errorCode === 'INSUFFICIENT_BALANCE_WITHDRAW') {
        const currentBalance = errorData.currentBalance?.toFixed(2) || '0';
        errorMessage = t('insufficientBalanceWithdraw')
          .replace('${currentBalance}', `$${currentBalance}`);
      } else if ((errorData.errorVi || apiError).includes('Số dư không đủ')) {
        errorMessage = t('insufficientBalanceWithdraw')
          .replace('${currentBalance}', `$${userProfile?.balance?.toFixed(2) || '0'}`);
      }

      setToast({ message: errorMessage, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const saveBind = async () => {
    const isCard = bindForm.kind === 'card';
    const account_number = (isCard ? bindForm.account_number : bindForm.walletAddress).trim();
    const account_name = isCard ? bindForm.account_name.trim() : 'USDT';
    if (!account_number || (isCard && !account_name)) {
      setToast({ message: t('fillAllFields'), type: 'error' });
      return;
    }
    setSavingBind(true);
    try {
      await bankService.configBank({
        bank_name: isCard ? 'Visa' : `USDT-${bindForm.network}`,
        account_number,
        account_name,
        account_type: isCard ? 'visa' : 'usdt'
      });
      setToast({ message: t('bindSuccess'), type: 'success' });
      setBinding(false);
      const banksData = await bankService.getBanks();
      setBanks(banksData.banks);
      if (isCard) {
        const created = banksData.banks.find((b) => b.account_type !== 'usdt' && b.account_number === account_number);
        if (created) setFormData((prev) => ({ ...prev, withdrawMethod: 'bank', selectedBankId: created.id.toString() }));
      } else {
        setFormData((prev) => ({ ...prev, withdrawMethod: 'usdt', walletAddress: account_number, network: bindForm.network }));
      }
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setSavingBind(false);
    }
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      {visible && (
        <div className="fixed inset-0 z-[60]">
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${animating ? 'opacity-50' : 'opacity-0'}`}
            onClick={onClose}
          />
          <div
            className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-lift flex flex-col max-h-[92vh] transform transition-transform duration-300 ease-out ${
              animating ? 'translate-y-0' : 'translate-y-full'
            }`}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-3">
              <h2 className="text-lg font-bold text-ink">{t('withdrawals')}</h2>
              <button
                onClick={onClose}
                className="p-2 -mr-2 rounded-full text-ink-muted hover:bg-canvas transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <p className="text-ink-secondary">{t('loading')}</p>
                </div>
              ) : formData.withdrawMethod === 'bank' && cardBanks.length === 0 && !binding ? (
                <div className="px-4 pb-6">
                  <div className="rounded-xl bg-canvas overflow-hidden">
                    {usdtBound && (
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, withdrawMethod: 'usdt' }))}
                        className="w-full flex items-center gap-3 p-3.5 active:bg-gray-100 transition-colors"
                      >
                        <span className="w-9 h-9 shrink-0 rounded-full bg-white flex items-center justify-center">
                          <Wallet className="w-[17px] h-[17px] text-ink" strokeWidth={1.9} />
                        </span>
                        <span className="flex-1 text-left text-[13.5px] font-semibold text-ink">{t('goUsdtWithdraw')}</span>
                        <ChevronRight className="w-[17px] h-[17px] text-ink-muted" strokeWidth={2} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setBinding(true)}
                      className={`w-full flex items-center gap-3 p-3.5 active:bg-gray-100 transition-colors ${usdtBound ? 'border-t border-gray-200/70' : ''}`}
                    >
                      <span className="w-9 h-9 shrink-0 rounded-full bg-white flex items-center justify-center">
                        <Plus className="w-[17px] h-[17px] text-ink" strokeWidth={2} />
                      </span>
                      <span className="flex-1 text-left text-[13.5px] font-semibold text-ink">{t('addBinding')}</span>
                      <ChevronRight className="w-[17px] h-[17px] text-ink-muted" strokeWidth={2} />
                    </button>
                  </div>
                </div>
              ) : binding ? (
                <div className="px-4 pb-6 space-y-4">
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setBinding(false)}
                      aria-label={t('back')}
                      className="w-8 h-8 -ml-1 flex items-center justify-center rounded-full text-ink"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <p className="text-[15px] font-bold text-ink">{t('addBinding')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 rounded-full bg-canvas p-1">
                    <button type="button" onClick={() => setBindForm({ ...bindForm, kind: 'card' })} className={`rounded-full py-2 text-[13px] font-bold transition-colors ${bindForm.kind === 'card' ? 'bg-white text-ink shadow-sm' : 'text-ink-muted'}`}>{t('bindVisaCard')}</button>
                    <button type="button" onClick={() => setBindForm({ ...bindForm, kind: 'usdt' })} className={`rounded-full py-2 text-[13px] font-bold transition-colors ${bindForm.kind === 'usdt' ? 'bg-white text-ink shadow-sm' : 'text-ink-muted'}`}>{t('bindUsdtAddr')}</button>
                  </div>
                  {bindForm.kind === 'card' ? (
                    <>
                      <div>
                        <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('cardNumber')}</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={bindForm.account_number}
                          onChange={(e) => setBindForm({ ...bindForm, account_number: e.target.value.replace(/[^\d]/g, '').slice(0, 19) })}
                          className="w-full p-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-[14px] font-medium text-ink tabular-nums"
                          placeholder="4111 1111 1111 1111"
                        />
                      </div>
                      <div>
                        <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('holderName')}</label>
                        <input
                          type="text"
                          value={bindForm.account_name}
                          onChange={(e) => setBindForm({ ...bindForm, account_name: e.target.value })}
                          className="w-full p-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-[14px] text-ink"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('usdtNetwork')}</label>
                        <div className="grid grid-cols-3 gap-2">
                          {USDT_NETWORKS.map(([net, chain]) => (
                            <button
                              key={net}
                              type="button"
                              onClick={() => setBindForm({ ...bindForm, network: net })}
                              className={`rounded-xl py-2 flex flex-col items-center transition-colors ${bindForm.network === net ? 'bg-ink text-white' : 'bg-canvas text-ink-muted'}`}
                            >
                              <span className="text-[13px] font-bold">{net}</span>
                              <span className={`text-[10.5px] ${bindForm.network === net ? 'text-white/60' : 'text-ink-muted/70'}`}>{chain}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('usdtWalletAddress')}</label>
                        <input
                          type="text"
                          value={bindForm.walletAddress}
                          onChange={(e) => setBindForm({ ...bindForm, walletAddress: e.target.value })}
                          className="w-full p-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 font-mono text-[13px] text-ink"
                        />
                      </div>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={saveBind}
                    disabled={savingBind}
                    className="w-full h-12 rounded-full bg-brand-500 active:bg-brand-600 text-white text-[14px] font-bold transition-colors disabled:opacity-50"
                  >
                    {savingBind ? t('processing') : t('bindSave')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="px-4 pb-6 space-y-4">
                  <div className="grid grid-cols-2 gap-2 rounded-full bg-canvas p-1">
                    <button type="button" onClick={() => setFormData({ ...formData, withdrawMethod: 'bank' })} className={`rounded-full py-2.5 text-sm font-bold transition-colors ${formData.withdrawMethod === 'bank' ? 'bg-white text-ink shadow-sm' : 'text-ink-muted'}`}>{t('withdrawBank')}</button>
                    <button type="button" onClick={() => setFormData({ ...formData, withdrawMethod: 'usdt' })} className={`rounded-full py-2.5 text-sm font-bold transition-colors ${formData.withdrawMethod === 'usdt' ? 'bg-white text-ink shadow-sm' : 'text-ink-muted'}`}>USDT</button>
                  </div>

                  <div className="bg-canvas rounded-xl p-4">
                    <p className="text-[12px] text-ink-muted mb-1">{t('withdrawBalance')}</p>
                    <p className="text-[28px] leading-none font-extrabold text-money tabular-nums">
                      ${(userProfile?.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>

                  {formData.withdrawMethod === 'bank' ? <>
                    <div>
                      <label className="block text-sm font-medium text-ink mb-2">{t('selectRecipient')}</label>
                      <select value={formData.selectedBankId} onChange={(e) => setFormData({...formData, selectedBankId: e.target.value})} className="w-full p-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 font-medium text-ink" required>
                        {cardBanks.map((bank) => <option key={bank.id} value={bank.id}>{bank.bank_name} - {bank.account_number} ({bank.account_name})</option>)}
                      </select>
                    </div>
                    {selectedBank && <div className="bg-brand-50 rounded-xl p-4"><p className="text-sm font-semibold text-ink mb-2">{t('accountInfo')}</p><div className="space-y-1"><div className="flex justify-between"><span className="text-sm text-ink-secondary">{t('bank')}</span><span className="text-sm font-semibold text-ink">{selectedBank.bank_name}</span></div><div className="flex justify-between"><span className="text-sm text-ink-secondary">{t('accountNumber')}</span><span className="text-sm font-semibold text-ink tabular-nums">{selectedBank.account_number}</span></div><div className="flex justify-between"><span className="text-sm text-ink-secondary">{t('accountHolder')}</span><span className="text-sm font-semibold text-ink">{selectedBank.account_name}</span></div>{selectedBank.branch && <div className="flex justify-between"><span className="text-sm text-ink-secondary">{t('branch')}</span><span className="text-sm font-semibold text-ink">{selectedBank.branch}</span></div>}</div></div>}
                  </> : <>
                    <div>
                      <label className="block text-sm font-medium text-ink mb-2">{t('usdtNetwork')}</label>
                      <div className="grid grid-cols-3 gap-2">
                        {USDT_NETWORKS.map(([net, chain]) => (
                          <button
                            key={net}
                            type="button"
                            onClick={() => setFormData({ ...formData, network: net })}
                            className={`rounded-xl py-2 flex flex-col items-center transition-colors ${formData.network === net ? 'bg-ink text-white' : 'bg-canvas text-ink-muted'}`}
                          >
                            <span className="text-[13px] font-bold">{net}</span>
                            <span className={`text-[10.5px] ${formData.network === net ? 'text-white/60' : 'text-ink-muted/70'}`}>{chain}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div><label className="block text-sm font-medium text-ink mb-2">{t('usdtWalletAddress')}</label><input type="text" value={formData.walletAddress} onChange={(e) => setFormData({...formData, walletAddress: e.target.value})} className="w-full p-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 font-mono text-sm text-ink" placeholder={t('enterUsdtAddress')} required /></div>
                  </>}

                  <div>
                    <label className="block text-sm font-medium text-ink mb-2">{t('withdrawAmount')}</label>
                    <input
                      type="text"
                      value={formData.amount ? parseInt(formData.amount).toLocaleString('en-US') : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d]/g, '');
                        setFormData({...formData, amount: value});
                      }}
                      className="w-full p-4 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-lg font-bold text-ink tabular-nums"
                      placeholder={t('enterAmount')}
                      required
                    />
                    <p className="text-xs text-ink-muted mt-2">{t('withdrawMinimum')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink mb-2">{t('withdrawPin')}</label>
                    <input
                      type="password"
                      value={formData.withdrawPin}
                      onChange={(e) => setFormData({...formData, withdrawPin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      className="w-full p-4 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-center text-2xl tracking-widest font-bold text-ink"
                      placeholder="••••••"
                      maxLength="6"
                      required
                    />
                    <p className="text-xs text-ink-muted mt-2">{t('enterWithdrawPin')}</p>
                  </div>

                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">{t('withdrawWarning')}</p>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="flex-1 h-12 rounded-full bg-canvas text-ink font-bold hover:bg-gray-200 transition-colors"
                    >
                      {t('cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 h-12 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? t('processing') : t('confirmWithdraw')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WithdrawModal;
